// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Eye, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  AlertCircle, Filter, Flag, X
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Sighting Categories
const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'ROBBERY', label: 'Robbery' },
  { value: 'RAM_RAID', label: 'Ram Raid' },
  { value: 'BURGLARY', label: 'Burglary' },
  { value: 'THEFT', label: 'Theft' },
  { value: 'DANGEROUS_DRIVING', label: 'Dangerous Driving' },
  { value: 'HOSTILE_RECON', label: 'Hostile Recon' },
  { value: 'CLONED_PLATES', label: 'Cloned Plates' },
  { value: 'OTHER', label: 'Other' }
];

// Threat Level Badge
function ThreatBadge({ level }) {
  const colors = {
    0: { bg: '#E8F5E9', text: '#2E7D32' },
    1: { bg: '#FFF8E1', text: '#F57F17' },
    2: { bg: '#FFF3E0', text: '#E65100' },
    3: { bg: '#FFEBEE', text: '#C62828' },
    4: { bg: '#FFCDD2', text: '#B71C1C' },
    5: { bg: '#B71C1C', text: '#FFFFFF' }
  };
  const config = colors[level] || colors[0];
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      Level {level}
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4 w-8"><div className="h-4 w-4 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-6 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// Empty State
function EmptyState() {
  return (
    <div className="p-12 text-center">
      <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="font-medium text-gray-600">No sightings found</p>
      <p className="text-sm text-gray-400 mt-1">
        Adjust your filters or wait for officers to submit sightings
      </p>
    </div>
  );
}

// Expanded Row Details
function ExpandedDetails({ sighting }) {
  return (
    <tr className="bg-gray-50">
      <td colSpan="8" className="px-6 py-4">
        <div className="grid grid-cols-2 gap-6 max-w-4xl">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sighting Details</h4>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">Confidence:</span> <span className="font-medium">{sighting.confidence_level?.replace('_', ' ')}</span></p>
              <p><span className="text-gray-500">Police Incident #:</span> <span className="font-medium">{sighting.police_incident_number || 'N/A'}</span></p>
              <p><span className="text-gray-500">Created:</span> <span className="font-medium">{new Date(sighting.created_at).toLocaleString('en-GB')}</span></p>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Narrative</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {sighting.narrative || 'No narrative provided'}
            </p>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function SightingsPage() {
  const [sightings, setSightings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    category: '',
    threat_level: '',
    flagged: false,
    date_from: '',
    date_to: ''
  });

  const fetchSightings = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 10 });
      
      if (filters.category) params.append('category', filters.category);
      if (filters.threat_level) params.append('threat_level', filters.threat_level);
      if (filters.flagged) params.append('flagged', 'true');
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/sightings?${params.toString()}`);
      setSightings(response.data.sightings);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load sightings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSightings();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchSightings(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      threat_level: '',
      flagged: false,
      date_from: '',
      date_to: ''
    });
    fetchSightings(1);
  };

  const toggleExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const toggleFlag = async (sightingId) => {
    try {
      await api.patch(`/sightings/${sightingId}/flag`);
      await fetchSightings(pagination.page);
    } catch (err) {
      alert(err.message || 'Failed to update flag');
    }
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sightings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sightings</h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors border bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          style={activeFilterCount > 0 ? { backgroundColor: `${VCIN_COLORS.primary}10`, borderColor: VCIN_COLORS.primary, color: VCIN_COLORS.primary } : {}}
          data-testid="filter-btn"
        >
          <Filter className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span 
              className="ml-1 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200" data-testid="filters-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Threat Level</label>
              <select
                value={filters.threat_level}
                onChange={(e) => handleFilterChange('threat_level', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              >
                <option value="">All Levels</option>
                {[0, 1, 2, 3, 4, 5].map(level => (
                  <option key={level} value={level}>Level {level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.flagged}
                  onChange={(e) => handleFilterChange('flagged', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Flagged Only</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sightings-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">VCIN Ref</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">VRN</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Threat</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Officer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Site</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : sightings.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="8">
                    <EmptyState />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {sightings.map((sighting) => (
                  <React.Fragment key={sighting.id}>
                    <tr 
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        expandedRow === sighting.id ? 'bg-gray-50' : ''
                      } ${sighting.flagged_for_review ? 'bg-amber-50/50' : ''}`}
                      onClick={() => toggleExpand(sighting.id)}
                    >
                      <td className="px-6 py-4 w-8">
                        <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                          {expandedRow === sighting.id ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span 
                            className="font-mono text-sm font-medium"
                            style={{ color: VCIN_COLORS.primary }}
                          >
                            {sighting.vcin_reference}
                          </span>
                          {sighting.flagged_for_review && (
                            <Flag className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className="px-2 py-1 rounded text-sm font-bold"
                          style={{ 
                            backgroundColor: VCIN_COLORS.plateYellow,
                            fontFamily: 'monospace'
                          }}
                        >
                          {sighting.vrn}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sighting.primary_category?.replace(/_/g, ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ThreatBadge level={sighting.threat_level} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sighting.officer_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sighting.site_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(sighting.datetime).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                    </tr>
                    {expandedRow === sighting.id && (
                      <ExpandedDetails sighting={sighting} />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 10 + 1} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} sightings
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchSightings(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchSightings(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
